import React from 'react';
import styled from 'styled-components';
import useTranslations from '../hooks/useTranslations';
import LanguageSelector from '../components/LanguageSelector';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
`;

const Header = styled.div`
  max-width: 800px;
  margin: 0 auto 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: #333;
  font-size: 2.5rem;
  margin: 0;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.6;

  h2 {
    color: #FF6B6B;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  h3 {
    color: #333;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }

  p {
    margin-bottom: 1rem;
    color: #555;
  }

  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    color: #555;
  }
`;

const BackLink = styled.a`
  color: #FF6B6B;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyPolicyPage: React.FC = () => {
  const { t } = useTranslations();

  return (
    <PageContainer>
      <Header>
        <Title>{t('legal.privacy.title', 'Privacy Policy')}</Title>
        <LanguageSelector />
      </Header>
      
      <Content>
        <BackLink href="/">{t('legal.backToHome', '← Torna alla Home')}</BackLink>
        
        <h2>{t('legal.privacy.lastUpdated', 'Ultimo aggiornamento: 25 luglio 2025')}</h2>
        
        <p>{t('legal.privacy.intro', 'Questa Privacy Policy descrive come krea cloud, Inc. raccoglie, utilizza e protegge le informazioni quando utilizzi il servizio Sistema Gelato.')}</p>

        <h2>{t('legal.privacy.section1.title', '1. Informazioni che Raccogliamo')}</h2>
        <h3>{t('legal.privacy.section1.personal.title', 'Informazioni Personali')}</h3>
        <p>{t('legal.privacy.section1.personal.content', 'Raccogliamo informazioni che ci fornisci direttamente, come:')}</p>
        <ul>
          <li>{t('legal.privacy.section1.personal.item1', 'Nome e informazioni di contatto')}</li>
          <li>{t('legal.privacy.section1.personal.item2', 'Indirizzo email')}</li>
          <li>{t('legal.privacy.section1.personal.item3', 'Informazioni aziendali della gelateria')}</li>
          <li>{t('legal.privacy.section1.personal.item4', 'Dati di produzione e gestione')}</li>
        </ul>

        <h3>{t('legal.privacy.section1.usage.title', 'Informazioni di Utilizzo')}</h3>
        <p>{t('legal.privacy.section1.usage.content', 'Raccogliamo automaticamente informazioni su come utilizzi il nostro servizio, inclusi log di accesso, indirizzo IP e dati di navigazione.')}</p>

        <h2>{t('legal.privacy.section2.title', '2. Come Utilizziamo le Informazioni')}</h2>
        <p>{t('legal.privacy.section2.content', 'Utilizziamo le informazioni raccolte per:')}</p>
        <ul>
          <li>{t('legal.privacy.section2.item1', 'Fornire e mantenere il servizio Sistema Gelato')}</li>
          <li>{t('legal.privacy.section2.item2', 'Migliorare e personalizzare la tua esperienza')}</li>
          <li>{t('legal.privacy.section2.item3', 'Comunicare con te riguardo al servizio')}</li>
          <li>{t('legal.privacy.section2.item4', 'Garantire la sicurezza del servizio')}</li>
        </ul>

        <h2>{t('legal.privacy.section3.title', '3. Condivisione delle Informazioni')}</h2>
        <p>{t('legal.privacy.section3.content', 'Non vendiamo, affittiamo o condividiamo le tue informazioni personali con terze parti, eccetto nei seguenti casi:')}</p>
        <ul>
          <li>{t('legal.privacy.section3.item1', 'Con il tuo consenso esplicito')}</li>
          <li>{t('legal.privacy.section3.item2', 'Per rispettare obblighi legali')}</li>
          <li>{t('legal.privacy.section3.item3', 'Per proteggere i nostri diritti e sicurezza')}</li>
        </ul>

        <h2>{t('legal.privacy.section4.title', '4. Sicurezza dei Dati')}</h2>
        <p>{t('legal.privacy.section4.content', 'Implementiamo misure di sicurezza appropriate per proteggere le tue informazioni contro accesso non autorizzato, alterazione, divulgazione o distruzione.')}</p>

        <h2>{t('legal.privacy.section5.title', '5. I Tuoi Diritti')}</h2>
        <p>{t('legal.privacy.section5.content', 'Hai il diritto di:')}</p>
        <ul>
          <li>{t('legal.privacy.section5.item1', 'Accedere alle tue informazioni personali')}</li>
          <li>{t('legal.privacy.section5.item2', 'Correggere informazioni inesatte')}</li>
          <li>{t('legal.privacy.section5.item3', 'Richiedere la cancellazione dei tuoi dati')}</li>
          <li>{t('legal.privacy.section5.item4', 'Opporti al trattamento dei tuoi dati')}</li>
        </ul>

        <h2>{t('legal.privacy.section6.title', '6. Contatti')}</h2>
        <p>{t('legal.privacy.section6.content', 'Per domande riguardo questa Privacy Policy, contattaci a:')}</p>
        <p>
          <strong>krea cloud, Inc.</strong><br />
          651 N Broad St, Suite 201<br />
          Middletown, Delaware 19709<br />
          Email: info@omai.cloud<br />
          Telefono (Italia): +39 347 128 1124
        </p>
      </Content>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;
